import React, { useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { AlertTriangle, RefreshCw, Copy, X, Trash2, RotateCw } from "lucide-react";
import {
  CONFLICT_CLASS,
  classifyConflict,
  friendlyStatusLabel,
  formatVersionDelta,
} from "./conflictDiff";
import { friendlyPathLabel } from "./cascadePreview";

/**
 * ConflictDiffModal — QUESTIONNAIRE-2.0-A-S2-γ-D-A (stand-alone).
 *
 * Stand-alone modal that surfaces a 409 conflict so the admin can decide
 * what to do without losing their attempted change. Pure presentational +
 * lightweight state for the inner "are-you-sure" cancel guard.
 *
 * NOT wired into the Builder yet — γ-D-B will integrate it.
 *
 * Props:
 *   open                Boolean — controls visibility.
 *   mode                "value" | "status" — selects which sections render.
 *   attempt             { path, value?, status?, expected_previous_status?, cachedRemoteValue? }
 *   conflictPayload     { error, current_version, expected_version, ...} from the 409 body.
 *   remoteSnapshot      { version, valueAtPath?, statusAtPath?, lastEditedBy?, updatedAt? }
 *                       Filled by the parent AFTER reloading the doc.
 *   busy                Boolean — disables actions while a parent operation runs.
 *   onReload            () => Promise<void> | void — refresh remote snapshot.
 *   onCancel            () => void — close modal (no further action).
 *   onCopyAttempt       () => void — caller-managed copy implementation (clipboard).
 *   onDiscard           () => void — discard attempt + close modal.
 *   onRetry             () => Promise<void> | void — re-send patch (only when canRetry).
 *
 * Out of scope γ-D-A:
 *   - Fetching anything. Parent owns network calls.
 *   - Calling PATCH directly.
 *   - Auto-refresh on a timer (γ-D-D).
 */
const ConflictDiffModal = ({
  open,
  mode = "value",
  attempt = null,
  conflictPayload = null,
  remoteSnapshot = null,
  busy = false,
  onReload = () => {},
  onCancel = () => {},
  onCopyAttempt = () => {},
  onDiscard = () => {},
  onRetry = () => {},
}) => {
  // Inner state: a tiny confirm-on-discard / confirm-on-close guard. We do
  // NOT need a reset effect because the parent controls remount via `key`.
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  const classification = useMemo(
    () => classifyConflict({ attempt, conflictPayload, remoteSnapshot }),
    [attempt, conflictPayload, remoteSnapshot],
  );

  const path = attempt?.path || conflictPayload?.path || "";
  const friendly = useMemo(() => (path ? friendlyPathLabel(path) : "(unknown path)"), [path]);

  const localVersion = conflictPayload?.expected_version;
  const remoteVersion = conflictPayload?.current_version ?? remoteSnapshot?.version;
  const versionLine = formatVersionDelta(localVersion, remoteVersion);

  const isStatusMode = mode === "status";
  const isValueMode = mode === "value";

  const attemptedValue = attempt?.value;
  const remoteValue = remoteSnapshot?.valueAtPath;
  const cachedValue = attempt?.cachedRemoteValue;

  const attemptedStatus = attempt?.status;
  const expectedPrevStatus = attempt?.expected_previous_status;
  const remoteStatus = conflictPayload?.current_status ?? remoteSnapshot?.statusAtPath;

  const hasAttemptValue =
    typeof attemptedValue === "string" && attemptedValue.length > 0;
  const longAttempt = hasAttemptValue && attemptedValue.length >= 50;

  // Wire close handler: ask before discarding when attempt is non-trivial.
  const handleCloseRequest = () => {
    if (busy) return;
    if (longAttempt && !confirmDiscard) {
      setConfirmDiscard(true);
      return;
    }
    onCancel();
  };

  const handleDiscardRequest = () => {
    if (busy) return;
    if (longAttempt && !confirmDiscard) {
      setConfirmDiscard(true);
      return;
    }
    setConfirmDiscard(false);
    onDiscard();
  };

  return (
    <Dialog
      open={!!open}
      onOpenChange={(o) => { if (!o) handleCloseRequest(); }}
    >
      <DialogContent
        className="sm:max-w-2xl"
        data-testid="conflict-diff-modal"
        data-conflict-class={classification.kind}
        data-mode={mode}
        data-path={path}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-rose-900">
            <AlertTriangle className="w-5 h-5 text-rose-600" />
            Version Conflict — <span className="font-mono text-sm">{friendly}</span>
          </DialogTitle>
          <DialogDescription className="pt-1 text-slate-600">
            Another admin updated this questionnaire while you were editing.
            Review the differences below before continuing — your draft is preserved.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {/* Version line + classification banner */}
          <div
            data-testid="conflict-diff-modal-version-info"
            className="rounded-md border border-slate-200 bg-slate-50 px-3 py-1.5 text-[11px] text-slate-700"
          >
            <span className="font-semibold uppercase tracking-wide text-slate-500">Version: </span>
            <span data-testid="conflict-diff-modal-version-delta">{versionLine}</span>
            {remoteSnapshot?.lastEditedBy && (
              <>
                {" · "}
                <span className="font-semibold uppercase tracking-wide text-slate-500">By: </span>
                <span data-testid="conflict-diff-modal-last-edited-by">{remoteSnapshot.lastEditedBy}</span>
              </>
            )}
            {remoteSnapshot?.updatedAt && (
              <>
                {" · "}
                <span data-testid="conflict-diff-modal-updated-at">{remoteSnapshot.updatedAt}</span>
              </>
            )}
          </div>

          <div
            data-testid="conflict-diff-modal-classification"
            data-canretry={classification.canRetry ? "true" : "false"}
            className={`rounded-md border px-3 py-2 text-xs ${
              classification.kind === CONFLICT_CLASS.VERSION_RETRY_SAFE
                ? "bg-emerald-50 border-emerald-300 text-emerald-900"
                : classification.kind === CONFLICT_CLASS.UNKNOWN
                  ? "bg-slate-50 border-slate-300 text-slate-800"
                  : "bg-amber-50 border-amber-300 text-amber-900"
            }`}
          >
            <span data-testid="conflict-diff-modal-classification-reason">{classification.reason}</span>
          </div>

          {/* VALUE diff section */}
          {isValueMode && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div data-testid="conflict-diff-modal-attempt-panel">
                <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">
                  Your attempted change
                </div>
                <pre
                  data-testid="conflict-diff-modal-attempt-value"
                  className="max-h-40 overflow-y-auto whitespace-pre-wrap rounded-md border border-sky-300 bg-sky-50 px-2.5 py-2 text-xs text-slate-900"
                >
                  {hasAttemptValue ? attemptedValue : <em className="text-slate-400">(empty)</em>}
                </pre>
                {typeof cachedValue === "string" && (
                  <details className="mt-1 text-[10px] text-slate-500">
                    <summary
                      data-testid="conflict-diff-modal-cached-toggle"
                      className="cursor-pointer hover:text-slate-700"
                    >
                      Show original value you loaded ({cachedValue.length} chars)
                    </summary>
                    <pre
                      data-testid="conflict-diff-modal-cached-value"
                      className="mt-1 max-h-32 overflow-y-auto whitespace-pre-wrap rounded border border-slate-200 bg-white px-2 py-1.5 text-xs text-slate-600"
                    >
                      {cachedValue || <em>(empty)</em>}
                    </pre>
                  </details>
                )}
              </div>

              <div data-testid="conflict-diff-modal-remote-panel">
                <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">
                  Current remote value
                </div>
                <pre
                  data-testid="conflict-diff-modal-remote-value"
                  className="max-h-40 overflow-y-auto whitespace-pre-wrap rounded-md border border-rose-300 bg-rose-50 px-2.5 py-2 text-xs text-slate-900"
                >
                  {remoteValue !== undefined
                    ? (remoteValue || <em className="text-slate-400">(empty)</em>)
                    : <em className="text-slate-400">(remote not loaded)</em>}
                </pre>
              </div>
            </div>
          )}

          {/* STATUS diff section */}
          {isStatusMode && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="conflict-diff-modal-status-grid">
              <div data-testid="conflict-diff-modal-attempt-status-panel">
                <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">
                  Your attempted transition
                </div>
                <div className="flex items-center gap-1.5 rounded-md border border-sky-300 bg-sky-50 px-2.5 py-2 text-xs text-slate-900">
                  <span data-testid="conflict-diff-modal-attempt-prev-status" className="font-mono">
                    {friendlyStatusLabel(expectedPrevStatus)}
                  </span>
                  <span className="text-slate-400">→</span>
                  <span data-testid="conflict-diff-modal-attempt-next-status" className="font-mono font-semibold">
                    {friendlyStatusLabel(attemptedStatus)}
                  </span>
                </div>
              </div>

              <div data-testid="conflict-diff-modal-remote-status-panel">
                <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">
                  Current remote status
                </div>
                <div className="flex items-center gap-1.5 rounded-md border border-rose-300 bg-rose-50 px-2.5 py-2 text-xs text-slate-900">
                  <span data-testid="conflict-diff-modal-remote-status" className="font-mono font-semibold">
                    {friendlyStatusLabel(remoteStatus)}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Confirm-on-discard inline guard */}
          {confirmDiscard && (
            <div
              data-testid="conflict-diff-modal-confirm-discard-guard"
              className="rounded-md border border-rose-300 bg-rose-50 px-3 py-2 text-xs text-rose-900"
            >
              You have an unsaved attempt ({attemptedValue?.length || 0} chars).{" "}
              Click <strong>Copy my attempt</strong> first if you might need it later.{" "}
              <button
                type="button"
                data-testid="conflict-diff-modal-confirm-discard-yes"
                onClick={() => { setConfirmDiscard(false); onDiscard(); }}
                className="ml-1 underline font-semibold hover:text-rose-700"
              >
                Yes, discard anyway
              </button>
              {" · "}
              <button
                type="button"
                data-testid="conflict-diff-modal-confirm-discard-cancel"
                onClick={() => setConfirmDiscard(false)}
                className="underline hover:text-rose-700"
              >
                Keep
              </button>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-2 flex-wrap">
          {/* Secondary actions */}
          <button
            type="button"
            disabled={busy}
            onClick={onReload}
            data-testid="conflict-diff-modal-reload"
            className="tsw-btn-secondary inline-flex items-center gap-1"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Reload remote
          </button>
          {isValueMode && (
            <button
              type="button"
              disabled={busy || !hasAttemptValue}
              onClick={onCopyAttempt}
              data-testid="conflict-diff-modal-copy"
              className="tsw-btn-secondary inline-flex items-center gap-1"
            >
              <Copy className="w-3.5 h-3.5" /> Copy my attempt
            </button>
          )}
          <button
            type="button"
            disabled={busy}
            onClick={handleCloseRequest}
            data-testid="conflict-diff-modal-cancel"
            className="tsw-btn-secondary inline-flex items-center gap-1"
          >
            <X className="w-3.5 h-3.5" /> Cancel
          </button>
          {/* Discard (destructive) */}
          <button
            type="button"
            disabled={busy}
            onClick={handleDiscardRequest}
            data-testid="conflict-diff-modal-discard"
            className="tsw-btn-secondary inline-flex items-center gap-1 !text-rose-700 !border-rose-300 hover:!bg-rose-50"
          >
            <Trash2 className="w-3.5 h-3.5" /> Discard my change
          </button>
          {/* Retry (primary, conditional) */}
          <button
            type="button"
            disabled={busy || !classification.canRetry}
            onClick={onRetry}
            data-testid="conflict-diff-modal-retry"
            data-canretry={classification.canRetry ? "true" : "false"}
            title={classification.canRetry ? "Refresh version and re-send your change" : classification.reason}
            className={`inline-flex items-center gap-1 ${classification.canRetry ? "tsw-btn-primary" : "tsw-btn-secondary opacity-50 cursor-not-allowed"}`}
          >
            <RotateCw className="w-3.5 h-3.5" /> Reload & retry
          </button>
          {!classification.canRetry && (
            <span
              data-testid="conflict-diff-modal-retry-blocked-reason"
              className="basis-full text-[10px] text-slate-500 italic mt-1"
            >
              Retry blocked: {classification.reason}
            </span>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ConflictDiffModal;
